import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Heart, Loader2 } from 'lucide-react';
import { useProperties } from '../hooks/useSanityData';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';

const SavedPropertiesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedNavigation();
  const { properties: allProperties, loading } = useProperties();

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-10 w-10 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('saved.saveYourFavorites')}</h2>
            <p className="text-gray-600 mb-8">
              {t('saved.loginToSave')}
            </p>
            <Button
              onClick={() => navigate(getLocalizedPath('/login'))}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {t('saved.loginToSaveButton')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const savedProperties = allProperties.filter((property) =>
    user.savedProperties?.includes(property.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-100 p-3 rounded-lg">
              <Heart className="h-8 w-8 text-red-500 fill-current" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{t('saved.title')}</h1>
              <p className="text-gray-600 mt-1">
                {savedProperties.length} {savedProperties.length === 1 ? t('common.property') : t('common.properties')} {t('common.saved')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {savedProperties.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="bg-gray-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-12 w-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('saved.noSavedHomes')}</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {t('saved.startExploring')}
            </p>
            <Button
              onClick={() => navigate(getLocalizedPath('/properties'))}
              className="bg-blue-600 hover:bg-blue-700 h-12 px-8"
            >
              {t('saved.browseProperties')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedPropertiesPage;
